import React, { createContext, useContext } from 'react';
import type { PropsWithChildren } from 'react';
import type { FieldError, FieldErrors, Merge } from 'react-hook-form';
import styled from 'styled-components';

import { VerticalLayout } from '../../Layout';
import { Text, Title } from '../../Text';
import { useFormLabelWidth } from '../Form';

export const FormElement = ({
    children,
    $isVertical,
    $hideLabel,
}: PropsWithChildren<{ $isVertical?: boolean; $hideLabel?: boolean }>) => {
    const labelWidth = useFormLabelWidth();

    return (
        <StyledFormElement $isVertical={$isVertical} $labelWidth={labelWidth} $hideLabel={$hideLabel}>
            {children}
        </StyledFormElement>
    );
};

const StyledFormElement = styled.div<{ $isVertical?: boolean; $labelWidth?: string; $hideLabel?: boolean }>`
    ${({ $hideLabel, $isVertical, $labelWidth = '160px' }) =>
        !$hideLabel &&
        `
        display: grid;
        align-items: center;
        grid-template-columns: ${$isVertical ? '1fr' : `${$labelWidth} 1fr`};
    `}
    position: relative;

    ${({ $isVertical }) => $isVertical && `row-gap: 8px`}
`;

interface Props {
    errorMessage?: string | FieldError | Merge<FieldError, FieldErrors<any>>;
    description?: string;
}

export const InputDescription = ({ errorMessage, description }: Props) => {
    if (!errorMessage && !description) {
        return null;
    }
    return (
        <InputDescriptionWrapper $gap={4}>
            {errorMessage && <Text $themeColor="negative">{errorMessage as string}</Text>}
            {description && (
                <Text $themeColor="subtle" helper $italic>
                    {description}
                </Text>
            )}
        </InputDescriptionWrapper>
    );
};

const InputDescriptionWrapper = styled(VerticalLayout)`
    grid-column-end: -1;
`;

export const FormLabel = styled.label`
    font-size: 1rem;
    font-weight: 300;
`;

/** Set to false by containers such as FormSheet that already pad their content horizontally. */
export const FormSectionInsetContext = createContext(true);

export const FormSection = ({
    title,
    hasDivider,
    children,
    disabled,
    className,
}: PropsWithChildren<{
    title?: string;
    hasDivider?: boolean;
    disabled?: boolean;
    className?: string;
}>) => {
    const inset = useContext(FormSectionInsetContext);

    return (
        <Content $gap={16} $hasDivider={hasDivider} $inset={inset} className={className}>
            {title && <Title content>{title}</Title>}
            <StyledFieldset disabled={disabled}>{children}</StyledFieldset>
        </Content>
    );
};

const StyledFieldset = styled.fieldset`
    display: contents;
`;

const Content = styled(VerticalLayout)<{ $hasDivider?: boolean; $inset: boolean }>`
    padding: 0 ${({ $inset }) => ($inset ? '30px' : '0')};
    ${({ $hasDivider, theme }) =>
        $hasDivider &&
        `
        border-top: 1px solid ${theme.border.light};
        padding-top: 24px;
        `};
`;
